import type { PropsWithChildren } from 'react';
import * as React from 'react';
import type { ItemSelectionProps } from './types';
import { css, styled } from 'styled-components';

import { Sheet, SheetContent, SheetOverlay, SheetPortal, tk, Typography } from '@sunwebgroup/ui-components';
import { useQuickSearchStore } from '../store';
import { useItemContext } from './context';

export function SheetItem({
  additionalSelectedItems,
  selectionFormattedText,
  children,
  disabled,
}: PropsWithChildren<ItemSelectionProps>) {
  const { id, title, placeholder } = useItemContext();
  const { setActiveItem, activeItemId } = useQuickSearchStore();

  const isActive = activeItemId === id;

  const handleButtonClick = () => {
    setActiveItem(isActive ? null : id);
  };

  const handleModalClose = () => {
    setActiveItem(null);
  };

  return (
    <>
      <Wrapper className="sheet-item" tabIndex={0} onClick={handleButtonClick} $isDisabled={Boolean(disabled)}>
        <Typography $variant="body-sm-bold" as="p">
          {title}
        </Typography>
        <SelectionTextWrapper>
          {selectionFormattedText ? (
            <SelectionText $variant="body-md" as="p">
              {selectionFormattedText}
            </SelectionText>
          ) : (
            <PlaceholderText $variant="body-md" as="p">
              {placeholder}
            </PlaceholderText>
          )}
          <Typography $variant="body-md" as="p">
            {additionalSelectedItems && additionalSelectedItems > 0 && `+${additionalSelectedItems}`}
          </Typography>
        </SelectionTextWrapper>
      </Wrapper>
      <Sheet open={isActive} onOpenChange={handleModalClose}>
        <SheetPortal>
          <SheetOverlay onClick={() => handleModalClose} />
          <SheetContent $side="right">{children}</SheetContent>
        </SheetPortal>
      </Sheet>
    </>
  );
}

const Wrapper = styled.button<{ $isDisabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  width: 100%;
  padding: ${tk('spacing_sm')} ${tk('spacing_md')};
  border-radius: ${tk('radius_large')};
  border: 2px solid ${tk('color_semantic_border')};
  background-color: ${tk('color_semantic_bg')};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      pointer-events: none;
      background-color: ${tk('color_semantic_bg_disabled')};
    `}
`;

const SelectionTextWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const SelectionText = styled(Typography)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PlaceholderText = styled(SelectionText)`
  color: ${tk('color_semantic_text_subdued')};
`;
